/**
 * Support Tickets API Client
 */

import { getHttpClient } from '@spcc/core'
import type {
  SupportTicket,
  SupportTicketStats,
  SupportTicketsFilter,
  PaginatedSupportTickets,
  CreateSupportTicketRequest,
  UpdateSupportTicketRequest,
} from '../../domain/types'
import { SUPPORT_ENDPOINTS, DEFAULT_PAGE_SIZE } from '../../domain/constants'
import {
  supportTicketsResponseSchema,
  supportTicketResponseSchema,
} from '../../api/api-types'
import {
  transformSupportTicketToDomain,
  transformSupportTicketsToDomain,
} from '../../api/transformers'

/** Matches `rest.maxLimit` in apps/strapi/config/api.ts — larger pages are silently clamped. */
const EXPORT_PAGE_SIZE = 500
/** Hard stop so an unexpectedly huge dataset can't hang the browser. */
export const EXPORT_MAX_TICKETS = 10_000

function buildPopulateParams(): Record<string, string> {
  return {
    'populate[user]': 'true',
    'populate[tenant]': 'true',
    'populate[factory]': 'true',
    'populate[attachments]': 'true',
  }
}

function buildFilterParams(filter?: SupportTicketsFilter): Record<string, string> {
  const params: Record<string, string> = {}

  if (!filter) return params

  if (filter.search) {
    params['filters[$or][0][ticketNumber][$containsi]'] = filter.search
    params['filters[$or][1][subject][$containsi]'] = filter.search
    params['filters[$or][2][description][$containsi]'] = filter.search
  }

  if (filter.status && filter.status.length > 0) {
    filter.status.forEach((s, i) => {
      params[`filters[status][$in][${i}]`] = s
    })
  }

  if (filter.channel) {
    params['filters[channel][$eq]'] = filter.channel
  }

  if (filter.priority) {
    params['filters[priority][$eq]'] = filter.priority
  }

  if (filter.category) {
    params['filters[category][$eq]'] = filter.category
  }

  return params
}

function buildPaginationParams(filter?: SupportTicketsFilter): Record<string, string> {
  const page = filter?.page ?? 1
  const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE

  return {
    'pagination[page]': String(page),
    'pagination[pageSize]': String(pageSize),
  }
}

function buildSortParams(filter?: SupportTicketsFilter): Record<string, string> {
  const sortBy = filter?.sortBy ?? 'createdAt'
  const sortOrder = filter?.sortOrder ?? 'desc'
  return { sort: `${sortBy}:${sortOrder}` }
}

export const supportApi = {
  async uploadFiles(files: File[]): Promise<number[]> {
    const http = getHttpClient()
    const formData = new FormData()
    for (const file of files) {
      formData.append('files', file)
    }
    const response = await http.post('/api/upload', formData)
    const uploaded = response.data as Array<{ id: number }>
    return uploaded.map((f) => f.id)
  },

  async getTickets(filter?: SupportTicketsFilter): Promise<PaginatedSupportTickets> {
    const http = getHttpClient()
    const params = {
      ...buildPopulateParams(),
      ...buildFilterParams(filter),
      ...buildPaginationParams(filter),
      ...buildSortParams(filter),
    }

    const response = await http.get(SUPPORT_ENDPOINTS.BASE, { params })
    const parsed = supportTicketsResponseSchema.parse(response.data)

    return {
      data: transformSupportTicketsToDomain(parsed.data),
      pagination: parsed.meta?.pagination ?? {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        pageCount: 1,
        total: parsed.data.length,
      },
    }
  },

  /**
   * Every ticket matching `filter`, for reporting/export.
   *
   * Pages through the normal list endpoint so the existing role/tenant scoping in
   * the Strapi `tenant-isolation` middleware applies unchanged. Capped so a runaway
   * dataset can never spin forever.
   */
  async getAllTickets(filter?: SupportTicketsFilter): Promise<SupportTicket[]> {
    const tickets: SupportTicket[] = []
    let page = 1
    let pageCount = 1

    do {
      const result = await supportApi.getTickets({ ...filter, page, pageSize: EXPORT_PAGE_SIZE } as SupportTicketsFilter)
      tickets.push(...result.data)
      pageCount = result.pagination.pageCount
      page += 1
    } while (page <= pageCount && tickets.length < EXPORT_MAX_TICKETS)

    return tickets
  },

  async getTicket(documentId: string): Promise<SupportTicket> {
    const http = getHttpClient()
    const params = buildPopulateParams()

    const response = await http.get(`${SUPPORT_ENDPOINTS.BASE}/${documentId}`, { params })
    const parsed = supportTicketResponseSchema.parse(response.data)

    return transformSupportTicketToDomain(parsed.data)
  },

  async createTicket(data: CreateSupportTicketRequest): Promise<SupportTicket> {
    const http = getHttpClient()

    const response = await http.post(SUPPORT_ENDPOINTS.BASE, { data })
    const parsed = supportTicketResponseSchema.parse(response.data)

    return transformSupportTicketToDomain(parsed.data)
  },

  async updateTicket(documentId: string, data: UpdateSupportTicketRequest): Promise<SupportTicket> {
    const http = getHttpClient()

    const response = await http.put(`${SUPPORT_ENDPOINTS.BASE}/${documentId}`, { data })
    const parsed = supportTicketResponseSchema.parse(response.data)

    return transformSupportTicketToDomain(parsed.data)
  },

  async getTicketStats(filter?: Partial<SupportTicketsFilter>): Promise<SupportTicketStats> {
    const http = getHttpClient()
    const params: Record<string, string> = {
      'pagination[pageSize]': '1',
    }

    if (filter?.channel) {
      params['filters[channel][$eq]'] = filter.channel
    }

    // Fetch counts per status
    const statusCounts = { open: 0, inProgress: 0, resolved: 0, total: 0 }

    const statuses = ['open', 'in_progress', 'resolved', 'closed', 'waiting_on_reporter'] as const
    const requests = statuses.map(async (status) => {
      const statusParams = {
        ...params,
        'filters[status][$eq]': status,
      }
      if (filter?.channel) {
        statusParams['filters[channel][$eq]'] = filter.channel
      }
      const response = await http.get(SUPPORT_ENDPOINTS.BASE, { params: statusParams })
      const parsed = supportTicketsResponseSchema.parse(response.data)
      return { status, count: parsed.meta?.pagination?.total ?? 0 }
    })

    const results = await Promise.all(requests)

    for (const { status, count } of results) {
      if (status === 'open') statusCounts.open = count
      else if (status === 'in_progress') statusCounts.inProgress = count
      else if (status === 'resolved') statusCounts.resolved = count
      statusCounts.total += count
    }

    return statusCounts
  },
}
